/**
 * F45 (Wave 2D) — admin analytics & reporting API client.
 *
 * Endpoints (all under `/admin/analytics`, gated by `analytics:read`; the
 * alert-rule PATCH additionally needs `analytics:manage_alerts`):
 *   GET   /admin/analytics/dashboard                          → getDashboard            { data: AnalyticsDashboard }
 *   GET   /admin/analytics/funnel                             → getFunnel               { data: AnalyticsFunnelResult }
 *   GET   /admin/analytics/retention                          → getRetention            { data: AnalyticsRetention }
 *   GET   /admin/analytics/scorecards/{venues|sports|trainers}/:id → getScorecard       { data: AnalyticsScorecard }
 *   GET   /admin/analytics/geographic                         → getGeographic           { data: AnalyticsGeographic }
 *   GET   /admin/analytics/notification-engagement            → getNotificationEngagement { data: AnalyticsNotificationEngagement }
 *   POST  /admin/analytics/refresh                            → refresh                 { data: AnalyticsRefreshResult }
 *   GET   /admin/analytics/alert-rules                        → listAlertRules          { data: AnalyticsAlertRule[] }
 *   PATCH /admin/analytics/alert-rules/:ruleType              → updateAlertRule         { data: AnalyticsAlertRule }
 *
 * Response envelopes are `{ data: <obj> }` everywhere (the Wave-2A/2B routes).
 * Permission gating is server-side; the page-level <ProtectedRoute> gates which
 * routes an admin role can navigate to. The vitest config aliases this module
 * to `analytics.fixture` during tests (mirrors the F23/F44 split).
 *
 * NOTE: GET /admin/analytics/top exists server-side but has NO client method —
 * the dashboard's five Top-N tables come embedded in getDashboard. The unused
 * `listTopN` wrapper was removed (dead code); the route stays for API
 * consumers/tests and a future metric drill-down (re-add the wrapper then).
 *
 * READ-ONLY usage analytics. Returns AGGREGATES only — never an individual-user
 * identifier (D15).
 */
import type {
  AnalyticsAlertRule,
  AnalyticsAlertType,
  AnalyticsDashboard,
  AnalyticsFunnelKey,
  AnalyticsFunnelResult,
  AnalyticsGeographic,
  AnalyticsNotificationChannel,
  AnalyticsNotificationEngagement,
  AnalyticsRetention,
  AnalyticsScorecard,
  UpdateAnalyticsAlertRuleInput,
} from '@bravemove/types';
import { adminFetch, buildQuery } from '../auth/apiClient';

interface DataEnvelope<T> {
  readonly data: T;
}

// ─── Query option types ──────────────────────────────────────────────────────

export type AnalyticsSegmentDim = 'all' | 'sport' | 'city' | 'governorate';

export interface DashboardOpts {
  /** ISO `YYYY-MM-DD` (Asia/Beirut) inclusive lower bound. */
  readonly from: string;
  /** ISO `YYYY-MM-DD` (Asia/Beirut) inclusive upper bound. */
  readonly to: string;
  /** Compare-to-previous toggle — populates `previous`/`deltaPercent` per KPI + funnel step. */
  readonly compare?: boolean;
  readonly segmentDim?: AnalyticsSegmentDim;
  readonly segmentVal?: string;
}

export interface FunnelOpts {
  readonly funnel: AnalyticsFunnelKey;
  readonly from: string;
  readonly to: string;
  readonly compare?: boolean;
  readonly segmentDim?: 'all' | 'sport' | 'city';
  readonly segmentVal?: string;
}

export interface RetentionOpts {
  readonly from: string;
  readonly to: string;
  readonly segmentDim?: 'all' | 'sport' | 'city';
  readonly segmentVal?: string;
}

export interface ScorecardOpts {
  readonly from: string;
  readonly to: string;
  readonly compare?: boolean;
}

export interface GeographicOpts {
  readonly from: string;
  readonly to: string;
}

export interface NotificationEngagementOpts {
  readonly from: string;
  readonly to: string;
  readonly category?: string;
  readonly channel?: AnalyticsNotificationChannel;
}

export type ScorecardEntity = 'venues' | 'sports' | 'trainers';

// ─── API surface ─────────────────────────────────────────────────────────────

export const adminAnalyticsApi = {
  /** GET /admin/analytics/dashboard — 12 KPIs + trends + funnel summary + sport-mix + geo + heatmap + top-N. */
  getDashboard: async (opts: DashboardOpts): Promise<AnalyticsDashboard> => {
    const body = await adminFetch<DataEnvelope<AnalyticsDashboard>>(
      `/admin/analytics/dashboard${buildQuery({
        from: opts.from,
        to: opts.to,
        compare: opts.compare ? true : undefined,
        segmentDim: opts.segmentDim,
        segmentVal: opts.segmentVal,
      })}`,
    );
    return body.data;
  },

  /** GET /admin/analytics/funnel — one funnel's steps + conversions + drop-offs. */
  getFunnel: async (opts: FunnelOpts): Promise<AnalyticsFunnelResult> => {
    const body = await adminFetch<DataEnvelope<AnalyticsFunnelResult>>(
      `/admin/analytics/funnel${buildQuery({
        funnel: opts.funnel,
        from: opts.from,
        to: opts.to,
        compare: opts.compare ? true : undefined,
        segmentDim: opts.segmentDim,
        segmentVal: opts.segmentVal,
      })}`,
    );
    return body.data;
  },

  /** GET /admin/analytics/retention — D1/D7/D30 cohort curves by signup week. */
  getRetention: async (opts: RetentionOpts): Promise<AnalyticsRetention> => {
    const body = await adminFetch<DataEnvelope<AnalyticsRetention>>(
      `/admin/analytics/retention${buildQuery({
        from: opts.from,
        to: opts.to,
        segmentDim: opts.segmentDim,
        segmentVal: opts.segmentVal,
      })}`,
    );
    return body.data;
  },

  /** GET /admin/analytics/scorecards/{venues|sports|trainers}/:id. */
  getScorecard: async (
    entity: ScorecardEntity,
    id: string,
    opts: ScorecardOpts,
  ): Promise<AnalyticsScorecard> => {
    const body = await adminFetch<DataEnvelope<AnalyticsScorecard>>(
      `/admin/analytics/scorecards/${entity}/${encodeURIComponent(id)}${buildQuery({
        from: opts.from,
        to: opts.to,
        compare: opts.compare ? true : undefined,
      })}`,
    );
    return body.data;
  },

  /** GET /admin/analytics/geographic — city-level bookings (Postgres). */
  getGeographic: async (opts: GeographicOpts): Promise<AnalyticsGeographic> => {
    const body = await adminFetch<DataEnvelope<AnalyticsGeographic>>(
      `/admin/analytics/geographic${buildQuery({ from: opts.from, to: opts.to })}`,
    );
    return body.data;
  },

  /** GET /admin/analytics/notification-engagement — F26 open/tap by category × channel. */
  getNotificationEngagement: async (
    opts: NotificationEngagementOpts,
  ): Promise<AnalyticsNotificationEngagement> => {
    const body = await adminFetch<DataEnvelope<AnalyticsNotificationEngagement>>(
      `/admin/analytics/notification-engagement${buildQuery({
        from: opts.from,
        to: opts.to,
        category: opts.category,
        channel: opts.channel,
      })}`,
    );
    return body.data;
  },

  /**
   * POST /admin/analytics/refresh — manual rollup recompute. Rate-limited
   * 1/5min/admin (429 ANALYTICS_REFRESH_RATE_LIMITED on a too-frequent retap).
   */
  refresh: async (): Promise<{
    readonly lastDailyRefreshedAt: string;
    readonly lastTodayRefreshedAt: string;
  }> => {
    const body = await adminFetch<
      DataEnvelope<{ lastDailyRefreshedAt: string; lastTodayRefreshedAt: string }>
    >(`/admin/analytics/refresh`, { method: 'POST', body: {} as Record<string, unknown> });
    return body.data;
  },

  /** GET /admin/analytics/alert-rules — the 3 configurable rules. */
  listAlertRules: async (): Promise<readonly AnalyticsAlertRule[]> => {
    const body = await adminFetch<DataEnvelope<readonly AnalyticsAlertRule[]>>(
      `/admin/analytics/alert-rules`,
    );
    return body.data;
  },

  /**
   * PATCH /admin/analytics/alert-rules/:ruleType — update threshold/enabled/dow/hour.
   * Requires `analytics:manage_alerts` (super_admin only).
   */
  updateAlertRule: async (
    ruleType: AnalyticsAlertType,
    input: UpdateAnalyticsAlertRuleInput,
  ): Promise<AnalyticsAlertRule> => {
    const body = await adminFetch<DataEnvelope<AnalyticsAlertRule>>(
      `/admin/analytics/alert-rules/${encodeURIComponent(ruleType)}`,
      { method: 'PATCH', body: input as unknown as Record<string, unknown> },
    );
    return body.data;
  },
};
